import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import {
  DEFAULT_BRANDING_COLORS,
  applyBranding,
  parseBrandingColors,
  type BrandingColors,
} from '@/lib/branding';

export interface BrandInfo {
  name: string;
  logo: string | null;
  colors: BrandingColors;
}

interface BrandContextType {
  brand: BrandInfo;
  loading: boolean;
  refresh: () => void;
}

const BrandContext = createContext<BrandContextType | null>(null);

const DEFAULT_BRAND: BrandInfo = {
  name: 'ZIFEK',
  logo: null,
  colors: DEFAULT_BRANDING_COLORS,
};

export function BrandProvider({ children }: { children: ReactNode }) {
  const [brand, setBrand] = useState<BrandInfo>(DEFAULT_BRAND);
  const [loading, setLoading] = useState(true);

  const fetchBrand = useCallback(async () => {
    try {
      const { data } = await supabase
        .from('zifek')
        .select('title, lienimg, branding')
        .limit(1)
        .maybeSingle();

      if (data) {
        const colors = parseBrandingColors((data as { branding?: unknown }).branding);
        applyBranding(colors);
        setBrand({
          name: data.title || 'ZIFEK',
          logo: data.lienimg || null,
          colors,
        });
        return;
      }
      applyBranding(DEFAULT_BRANDING_COLORS);
    } catch {
      // On conserve la charte par défaut en cas d'échec de lecture.
      applyBranding(DEFAULT_BRANDING_COLORS);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBrand();
  }, [fetchBrand]);

  return (
    <BrandContext.Provider value={{ brand, loading, refresh: fetchBrand }}>
      {children}
    </BrandContext.Provider>
  );
}

export function useBrand(): BrandContextType {
  const context = useContext(BrandContext);
  if (!context) {
    throw new Error('useBrand must be used within a BrandProvider');
  }
  return context;
}